import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { getSessionToken } from '../auth/session-token';
import { logger } from '../lib/logger';
import { useScopeStore } from '../store/scope-store';
import { streamOrigin } from './client';
import { parseSseFrames } from './sse-frames';

const log = logger.child({ module: 'live-query' });

/** Backoff between reconnects, in ms. Cloud Run rotates instances and closes
 *  streams as a matter of course, so a drop is routine rather than an error —
 *  the first retry is quick, and it settles at half a minute. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 15_000, 30_000];

/**
 * Keeps a TanStack Query cache entry live from a server-sent stream.
 *
 * Deliberately generic: it takes a path and a query key and writes whatever the
 * stream sends into that key. Anything the API can express as an
 * `Observable<T>` — captures on a board, persona feedback — becomes
 * realtime by adding an `@Sse()` route and calling this. Nothing here knows
 * about fix lists.
 *
 * The seed still comes from the ordinary `useQuery` beside it. That keeps the
 * first paint identical whether or not the stream connects, and means a
 * blocked or buffered stream degrades to what the app did before rather than
 * to an empty screen.
 *
 * `fetch` rather than `EventSource`, because EventSource cannot set headers and
 * the session is an `Authorization: Bearer` token, not a cookie (CLAUDE.md >
 * Auth). The alternative is minting a second short-lived credential to put in
 * the query string, the way the browser worker's ticket does — worth it there,
 * where a raw WebSocket URL is handed to a browser API, and not worth it here.
 */
export function useLiveQuery<T>({
  enabled = true,
  path,
  queryKey,
}: {
  enabled?: boolean;
  /** An `@Sse()` route, already including any query string. */
  path: string;
  queryKey: QueryKey;
}): void {
  const queryClient = useQueryClient();
  // Stable across renders so the effect does not reconnect on every one.
  const key = JSON.stringify(queryKey);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    let attempt = 0;
    let stopped = false;

    const connect = async (): Promise<void> => {
      const token = getSessionToken();
      if (!token) return;
      const teamId = useScopeStore.getState().selectedTeamId;

      // Around Firebase Hosting when configured to — see `streamOrigin`.
      const response = await fetch(`${streamOrigin}/api${path}`, {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          // The same scope header `apiFetch` sends. Without it a stream opened
          // while acting as a team is resolved as the personal account, and
          // every team-owned row it watches is refused.
          ...(teamId ? { 'x-team-id': teamId } : {}),
        },
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        // 401/403 will not fix themselves by retrying, and retrying a
        // forbidden stream forever is a good way to look like an attack.
        if (response.status < 500) {
          stopped = true;
          log.warn({ path, status: response.status }, 'live stream refused');
        }
        throw new Error(`stream failed: ${String(response.status)}`);
      }

      attempt = 0;
      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += value;
        const { frames, rest } = parseSseFrames(buffer);
        buffer = rest;

        for (const { data, event } of frames) {
          // The event name decides whether this is payload at all. Taking
          // every frame's data blindly wrote the heartbeat — `data: {}` — over
          // the cache entry, so a list became an object and every `.filter` on
          // it threw. Anything that is not an update is ignored by design:
          // a ping exists only to keep the connection warm.
          if (event !== 'update') continue;
          if (!data) continue;

          try {
            queryClient.setQueryData<T>(queryKey, JSON.parse(data) as T);
          } catch (err) {
            log.warn({ err, path }, 'could not read a live frame');
          }
        }
      }
    };

    const run = async () => {
      while (!stopped && !controller.signal.aborted) {
        try {
          await connect();
        } catch (err) {
          if (controller.signal.aborted || stopped) return;
          log.debug({ err, path }, 'live stream dropped, reconnecting');
        }
        if (stopped || controller.signal.aborted) return;
        const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]!;
        attempt += 1;
        await new Promise((resolve) => setTimeout(resolve, wait));
      }
    };

    void run();

    return () => {
      stopped = true;
      controller.abort();
    };
    // `key` stands in for queryKey, which is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, path, key, queryClient]);
}
